export interface EditorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export type ToolbarPlacement = 'above' | 'below' | 'left' | 'right';

export interface ToolbarPosition {
  placement: ToolbarPlacement;
  left: number;
  top: number;
}

const GAP = 8;

function fits(position: ToolbarPosition, width: number, height: number, bounds: EditorRect): boolean {
  return position.left >= bounds.left
    && position.top >= bounds.top
    && position.left + width <= bounds.right
    && position.top + height <= bounds.bottom;
}

export function rectsIntersect(a: EditorRect, b: EditorRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function intersects(position: ToolbarPosition, width: number, height: number, target: EditorRect): boolean {
  return rectsIntersect(
    { left: position.left, top: position.top, right: position.left + width, bottom: position.top + height, width, height },
    target,
  );
}

export function placeToolbar(target: EditorRect, toolbarWidth: number, toolbarHeight: number, bounds: EditorRect): ToolbarPosition {
  const centeredLeft = target.left + (target.width - toolbarWidth) / 2;
  const candidates: ToolbarPosition[] = [
    { placement: 'above', left: centeredLeft, top: target.top - toolbarHeight - GAP },
    { placement: 'below', left: centeredLeft, top: target.bottom + GAP },
    { placement: 'right', left: target.right + GAP, top: target.top + (target.height - toolbarHeight) / 2 },
    { placement: 'left', left: target.left - toolbarWidth - GAP, top: target.top + (target.height - toolbarHeight) / 2 },
  ];
  const safe = candidates.find((candidate) => fits(candidate, toolbarWidth, toolbarHeight, bounds) && !intersects(candidate, toolbarWidth, toolbarHeight, target));
  if (safe) return safe;

  // If the toolbar cannot fit inside the editor bounds, non-overlap is still
  // the hard invariant. Put it in the first available editor-chrome slot
  // outside the selection; the real UI may compact/overflow its controls
  // there, but it must never paint over authored content.
  const nonOverlapping = candidates.find((candidate) => !intersects(candidate, toolbarWidth, toolbarHeight, target));
  if (nonOverlapping) return nonOverlapping;
  return { placement: 'above', left: target.left, top: target.top - toolbarHeight - GAP };
}
